import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import {
  calculatePagePercentageAdjustments,
  getPercentageAdjustedValue,
  getPageFieldPositions,
} from '../../../lib/documentHelper';

import {
  getPDFPageCountFromPage,
  adjustFieldPostionsForPageBreaks
} from '../../../lib/exporterHelper';

import GridLayout from '../GridLayout';

const StyledWrapper = styled.div`
  display: flex;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
`;

const StyledBorderWrapper = styled.div`
  display: flex;
  flex: 1;
  border: ${(props) => props.border};
  padding: ${(props) => props.padding || 0}px;
  margin: ${(props) => props.margin || 0}px;
`;

const StyledPageFields = styled.div`
  display: flex;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

/**
 * ExportFormPage handles specific requirements around formatting
 * for PDF Export. For instance, it measures the fields to dispaly
 * all internal contents, it adjusts the pages to support proper
 * page-break points, etc.
 */
const ExportFormPage = (props) => {

  const {

    mode,

    //Container properties
    containerWidth = 816,
    containerHeight = 1056,

    //Page Properties
    width,
    height,
    cols,
    rowHeight,
    presentation,
    layout,
    margin,
    padding,
    borderWidth,
    borderColor,
    borderStyle,

    fields,

  } = props;

  /**
   * Handle container max width and height adjustments
   *
   * The page margin, padding, and border should be applied to the container and
   * reduce the max dimensions of the container element.
   *
   * Why is this? The container element determines the max dimensions that the
   * fields and PDF background can be within. The border, padding, and margin of
   * the page itself reduce that max dimension limit. Therefore we apply those
   * page properties to the container since that is what enforces those dimensions
   * for the page fields and PDF background.
   *
   * For instance if the container max width is 816px and the page has padding of
   * 4px (x2 for both sides) then the max width of the container should be reduced
   * to 804px. Then we can scale the PDF background and fields to fit within that
   * new 804px width limit.
   */
  const adjustedContainerWidth = getAdjustedDimension(containerWidth, padding, margin, borderWidth);

  /**
   * Why do we have duplicate arguments for width? With e-forms we only need to scale by width. We do not
   * need to worry about height because height is not restricted in e-form format. We pass width twice so
   * that we get the same percentage difference and always have the width adjustment percentage given to us.
   */
  const percentageDifference = calculatePagePercentageAdjustments(width, width, adjustedContainerWidth, adjustedContainerWidth);

  const adjustedPageWidth = getPercentageAdjustedValue(width, percentageDifference);

  /**
   * We join width and color to a single border style because chrome displays
   * the border as a two tone color if you use border-color and border-width
   * indvidually.
   */
  const pageBorderWidth = borderWidth !== undefined ? `${borderWidth}px` : '0px';
  const pageBorderColor = borderColor || 'transparent';
  const pageBorderStyle = borderStyle || 'solid';
  const pageBorder = `${pageBorderWidth} ${pageBorderStyle} ${pageBorderColor}`;

  /**
   * Start Field Helpers
   */
  const [fieldMeasurements, setFieldMeasurements] = useState({});
  const [formattedFields, setFormattedFields] = useState([]);
  const [totalExportHeight, setTotalExportHeight] = useState([]);

  useEffect(() => {

    const fieldPositions = getPageFieldPositions({
      fields,
      mode,
      presentation,
      fieldMeasurements
    });

    const formattedFields = adjustFieldPostionsForPageBreaks({
      fieldPositions,
      rowHeight,
      cols,
      height,
      padding,
      margin,
      borderWidth,
      layout
    });

    setFormattedFields(formattedFields);

    const pdfPageHeight = 1056;
    const pageBreakCount = getPDFPageCountFromPage(formattedFields || [], pdfPageHeight, rowHeight);

    setTotalExportHeight(pageBreakCount * pdfPageHeight);

  }, [fields, fieldMeasurements]);

  return (
    <StyledWrapper
      {...{
        width: containerWidth,
        height: totalExportHeight || containerHeight,
      }}
    >
      <StyledBorderWrapper
        {...{
          border: pageBorder,
          margin,
          padding
        }}
      >
        <StyledPageFields>
          <GridLayout
            {...props}
            fields={formattedFields}
            width={adjustedPageWidth}
            cols={cols}
            enableLazyLoadFields={false}
            enableVirtualFields={false}
            enableFieldMeasurementRendering
            onFieldMeasurementsCompleted={setFieldMeasurements}
          />
        </StyledPageFields>
      </StyledBorderWrapper>
    </StyledWrapper>
  );

};

/**
 * What is 2 used for?
 *
 * We do (* 2) to represent the number of sides of the page to take into account.
 *
 * For instance if you want to adjust the height of the page by subtracting the margin
 * from top and bottom of the page then you would use a multiple of 2.
 */
const getAdjustedDimension = (value, padding, margin, borderWidth) => {

  let pageValue = value;
  if (padding) pageValue -= (padding * 2);
  if (borderWidth) pageValue -= (borderWidth * 2);
  if (margin) pageValue -= (margin * 2);

  return pageValue;

};

export default ExportFormPage;
